import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import setExpirationDate from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SetExpirationDate";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    expirationDate: state.pages.rfqQuoteDetails.expirationDate,
    expirationDateError: state.pages.rfqQuoteDetails.expirationDateError,
    quoteSettings: getSettingsCollection(state).quoteSettings,
    language: state.context.session.language,
    hideStateRegion: (state.context.settings.settingsCollection as any).myAccountSettings?.hideStateRegion,
});

const mapDispatchToProps = {
    setExpirationDate,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsInformationStyles {
    detailsGridContainer?: GridContainerProps;
    expirationDateGridItem?: GridItemProps;
    expirationDateDatePicker?: DatePickerPresentationProps;
    expirationDateLabelText?: TypographyPresentationProps;
    jobNameGridItem?: GridItemProps;
    jobNameHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userGridItem?: GridItemProps;
    userHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    dateGridItem?: GridItemProps;
    dateLabelText?: TypographyPresentationProps;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToGridItem?: GridItemProps;
    shipToHeadingAndText?: SmallHeadingAndTextStyles;
    notesGridItem?: GridItemProps;
    line?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;

    headingAndTextStyles?: SmallHeadingAndTextStyles;
}

export const rfqQuoteDetailsInformationStyles: RfqQuoteDetailsInformationStyles = {
    detailsGridContainer: {
        gap: 20,
        css: css`
            margin-bottom: 20px;
        `,
    },
    expirationDateGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    expirationDateDatePicker: {
        cssOverrides: {
            datePicker: css`
                width: 200px;
            `,
        },
    },
    expirationDateLabelText: { weight: "bold", size: 14 },
    jobNameGridItem: { width: [6, 6, 4, 4, 4] },
    jobNameHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    salesRepGridItem: { width: [6, 6, 4, 4, 4] },
    salesRepHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    userGridItem: { width: [6, 6, 4, 4, 4] },
    userHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    statusGridItem: { width: [6, 6, 4, 4, 4] },
    statusHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    dateGridItem: {
        width: [6, 6, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    dateLabelText: { weight: "bold", size: 14 },
    customerGridItem: { width: 12 },
    customerHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    shipToGridItem: { width: 12 },
    shipToHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    notesGridItem: { width: 12 },
    notesHeadingAndText: {
        heading: { transform: "initial", weight: "bold", size: 14 },
    },
    line: {
        width: 12,
        css: css`
            &&& {
                border-top: 1px solid #d9d9d9;
                margin: 20px 0 0;
            }
        `,
    },
};

const styles = rfqQuoteDetailsInformationStyles;

const RfqQuoteDetailsInformation = ({
    quoteState,
    expirationDate,
    expirationDateError,
    setExpirationDate,
    quoteSettings,
    /* */
    language,
    hideStateRegion,
}: Props) => {
    const quote = quoteState.value as any;

    const expirationDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setExpirationDate({ expirationDate: selectedDay });
    };

    if (!quote) {
        return null;
    }

    const expirationDateLabel = translate(`${quote.isJobQuote ? "Job" : "Quote"} Expiration Date`);

    const renderChannel = (channel: string) => {
        switch (channel) {
            case "CARS":
                return translate("AIR Quote");
            case "EDI":
                return translate("EDI Quote");
            case "OCR":
                return translate("Sales Rep");
            case "WEB":
                return translate("Web Quote");
            case "SA":
                return translate("Scheduled Quote");
            default:
                return translate("Sales Rep");
        }
    };

    return (
        <GridContainer {...styles.detailsGridContainer} data-test-selector="rfqQuoteDetails_info">
            {quote.isEditable && (
                <GridItem {...styles.expirationDateGridItem} data-test-selector="rfqQuoteDetails_expirationDate">
                    <DatePicker
                        {...styles.expirationDateDatePicker}
                        label={expirationDateLabel}
                        selectedDay={expirationDate}
                        error={expirationDateError}
                        dateTimePickerProps={{
                            minDate: new Date(),
                            maxDate: quote.isJobQuote ? undefined : addDays(new Date(), quoteSettings.quoteExpireDays),
                            ...styles.expirationDateDatePicker?.dateTimePickerProps,
                        }}
                        onDayChange={expirationDateChangeHandler}
                    />
                </GridItem>
            )}
            {!quote.isEditable && quote.expirationDate && (
                <GridItem {...styles.expirationDateGridItem}>
                    <Typography {...styles.expirationDateLabelText}>{expirationDateLabel}</Typography>
                    <Typography as="p" data-test-selector="rfqQuoteDetails_expirationDate">
                        <LocalizedDateTime
                            dateTime={quote.expirationDate}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </GridItem>
            )}
            {quote.isJobQuote && (
                <GridItem {...styles.jobNameGridItem}>
                    <SmallHeadingAndText
                        heading={`${translate("Job Name")}: `}
                        text={quote.jobName}
                        extendedStyles={styles.headingAndTextStyles}
                    />
                </GridItem>
            )}
            <GridItem {...styles.salesRepGridItem} width={12}>
                <StyledWrapper>
                    <SmallHeadingAndText
                        heading={`${translate("Status")}: `}
                        text={quote.statusDisplay?.split?.("Quote")?.[1]}
                        extendedStyles={styles.headingAndTextStyles}
                        data-test-selector="status"
                    />

                    <SmallHeadingAndText
                        heading={`${translate("Channel")}: `}
                        text={renderChannel(quote.properties.orderChannel)}
                        extendedStyles={styles.headingAndTextStyles}
                        data-test-selector="channel"
                    />

                    <SmallHeadingAndText
                        heading={`${translate("Expiration date")}: `}
                        text={
                            quote.properties.expirationDate
                                ? getLocalizedDateTime({
                                      dateTime: new Date(quote.properties.expirationDate),
                                      language,
                                  })
                                : ""
                        }
                        extendedStyles={styles.headingAndTextStyles}
                        data-test-selector="exporation-date"
                    />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.line} />
            <GridItem {...styles.salesRepGridItem} width={12}>
                <Typography as="h4" variant="h4">
                    {translate("Quote details")}
                </Typography>
            </GridItem>
            <GridItem {...styles.userGridItem} width={12}>
                <StyledWrapper>
                    <SmallHeadingAndText
                        heading={`${translate("Customer")}: `}
                        text={quote.btCompanyName}
                        extendedStyles={styles.headingAndTextStyles}
                        data-test-selector="customer"
                    />

                    <SmallHeadingAndText
                        heading={`${translate("Customer P.O.")}: `}
                        text={quote.customerPO}
                        extendedStyles={styles.headingAndTextStyles}
                        data-test-selector="customer-po"
                    />

                    <SmallHeadingAndText
                        heading={`${translate("Ship To")}: `}
                        text={[
                            quote.stCompanyName,
                            quote.stAddress1,
                            quote.stAddress2,
                            quote.shipToCity,
                            !hideStateRegion ? quote.shipToState : undefined,
                            quote.shipToPostalCode,
                            quote.stCountry,
                        ]
                            .filter(Boolean)
                            .join(", ")}
                        extendedStyles={styles.headingAndTextStyles}
                        data-test-selector="ship-to"
                    />

                    <SmallHeadingAndText
                        heading={`${translate("Quote Number")}: `}
                        text={quote.quoteNumber || "Pending"}
                        extendedStyles={styles.headingAndTextStyles}
                        data-test-selector="quote-number"
                    />

                    <SmallHeadingAndText
                        heading={`${translate("Date Submitted")}: `}
                        text={
                            quote.orderDate
                                ? getLocalizedDateTime({ dateTime: new Date(quote.orderDate), language })
                                : ""
                        }
                        extendedStyles={styles.headingAndTextStyles}
                        data-test-selector="date-submitted"
                    />
                </StyledWrapper>
            </GridItem>
            {/* <GridItem {...styles.userGridItem}>
                <SmallHeadingAndText
                    heading={translate("User")}
                    text={quote.userName}
                    extendedStyles={styles.userHeadingAndText}
                    data-test-selector="user"
                />
            </GridItem>
            <GridItem {...styles.statusGridItem}>
                <SmallHeadingAndText
                    heading={translate("Status")}
                    text={quote.statusDisplay}
                    extendedStyles={styles.statusHeadingAndText}
                />
            </GridItem>
            {quote.orderDate && (
                <GridItem {...styles.dateGridItem}>
                    <Typography {...styles.dateLabelText}>{translate("Date Submitted")}</Typography>
                    <Typography as="p">
                        <LocalizedDateTime
                            dateTime={quote.orderDate}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </GridItem>
            )}
            {quote.billTo && (
                <GridItem {...styles.customerGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Customer")}
                        text={quote.customerName}
                        extendedStyles={styles.customerHeadingAndText}
                    />
                </GridItem>
            )}
            <GridItem {...styles.shipToGridItem} data-test-selector="rfqConfirmation_shipTo">
                <SmallHeadingAndText
                    heading={translate(quote.fulfillmentMethod === "PickUp" ? "Pick Up" : "Ship To")}
                    text={quote.shipToFullAddress}
                    extendedStyles={styles.shipToHeadingAndText}
                />
            </GridItem>
            {quote.notes.trim().length > 0 && (
                <GridItem {...styles.notesGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Notes")}
                        text={quote.notes}
                        extendedStyles={styles.notesHeadingAndText}
                    />
                </GridItem>
            )} */}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsInformation),
    definition: {
        displayName: "Details",
        allowedContexts: ["RfqQuoteDetailsPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;
